<template>
  <section class="oa-section">
    <div class="oa-section-header">
      <h2><i class="fa fa-calendar-plus-o"></i> Appointment Types</h2>
      <button class="oa-btn oa-btn-add" @click="openModal()">
        <i class="fa fa-plus"></i> Add New Type
      </button>
    </div>

    <!-- Location Selector (only if multiple paid locations) -->
    <LocationSelector
      v-if="hasMultipleLocations"
      v-model="selectedSiteId"
      :locations="paidLocations"
      :locationStatus="locationStatus"
      label="Configuring for:"
      :showCopyButton="true"
      :showCount="true"
      @copy-settings="showCopyModal"
    />

    <p class="oa-form-help" style="margin-bottom: 16px;">Configure appointment types that patients can book</p>

    <div class="oa-info-box oa-info-box-blue">
      <div class="oa-info-box-title">
        <i class="fa fa-info-circle"></i>
        <span>Appointment Types</span>
      </div>
      <p>Changes to appointment types are saved automatically when you add, edit, or delete them.</p>
    </div>

    <!-- Empty state: only when no types AND no templates visible -->
    <div v-if="localTypes.length === 0 && visibleTemplates.length === 0" class="oa-empty-state">
      <i class="fa fa-calendar-plus-o"></i>
      <p>No appointment types configured yet.</p>
      <p class="small">Click "Add New Type" to create your first appointment type.</p>
    </div>

    <!-- Template info banner -->
    <div v-if="showTemplates" class="oa-info-box oa-info-box-warning" style="margin-bottom: 16px;">
      <div class="oa-info-box-title">
        <i class="fa fa-lightbulb-o"></i>
        <span>Getting Started Templates</span>
      </div>
      <p>We've pre-populated common appointment types to help you get started. <strong>Edit</strong> each template to configure operatories/providers, or <strong>Delete</strong> any you don't need.</p>
    </div>

    <div class="oa-apt-list">
      <div v-for="(type, index) in displayTypes" :key="type.id || index" class="oa-apt-card" :class="{ 'oa-apt-card-template': type.isTemplate }">
        <!-- Template badge -->
        <div v-if="type.isTemplate" class="oa-template-badge">
          <i class="fa fa-file-o"></i> Template
        </div>

        <div class="oa-apt-card-body">
          <div class="oa-apt-card-main">
            <!-- Icon + Title -->
            <div class="oa-apt-title-row">
              <div class="oa-apt-icon" :class="getIconClass(type.name)">
                <i :class="getIconFA(type.name)"></i>
              </div>
              <div>
                <h4>{{ type.name || 'Unnamed Type' }}</h4>
                <div class="oa-apt-badges">
                  <span v-if="type.availableFor && type.availableFor.newPatients !== false" class="oa-badge oa-badge-green">New Patients</span>
                  <span v-if="type.availableFor && type.availableFor.existingPatients !== false" class="oa-badge oa-badge-blue">Existing Patients</span>
                </div>
              </div>
            </div>

            <p class="oa-apt-description">{{ type.description || 'No description provided' }}</p>

            <!-- Stats Row -->
            <div class="oa-apt-stats">
              <div class="oa-apt-stat">
                <div class="oa-apt-stat-icon oa-stat-purple">
                  <i class="fa fa-clock-o"></i>
                </div>
                <div>
                  <p class="oa-apt-stat-value">{{ type.duration || 30 }} min</p>
                  <p class="oa-apt-stat-label">Duration</p>
                </div>
              </div>
              <div class="oa-apt-stat">
                <div class="oa-apt-stat-icon oa-stat-green">
                  <i class="fa fa-file-text-o"></i>
                </div>
                <div>
                  <p class="oa-apt-stat-value">{{ (type.procedureCodes || []).length }}</p>
                  <p class="oa-apt-stat-label">Procedure Codes</p>
                </div>
              </div>
              <div class="oa-apt-stat">
                <div class="oa-apt-stat-icon" :class="type.isTemplate ? 'oa-stat-red' : 'oa-stat-orange'">
                  <i class="fa" :class="type.isTemplate ? 'fa-exclamation-circle' : 'fa-user'"></i>
                </div>
                <div>
                  <p v-if="type.isTemplate" class="oa-apt-stat-value oa-stat-required">REQUIRED</p>
                  <p v-else class="oa-apt-stat-value">{{ (type.operatoryProviders || []).length }}</p>
                  <p class="oa-apt-stat-label">{{ type.isTemplate ? 'Edit to Configure' : 'Operatory/Provider Combos' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="oa-apt-card-actions">
            <button class="oa-btn oa-btn-edit" @click="openModal(index, type.isTemplate)">
              <i class="fa fa-pencil"></i> Edit
            </button>
            <button
              v-if="!type.isTemplate"
              class="oa-btn oa-btn-link"
              @click="grabLink(type)"
              :disabled="linkCopiedId === type.id || linkGeneratingId === type.id"
            >
              <i class="fa" :class="linkGeneratingId === type.id ? 'fa-spinner fa-spin' : linkCopiedId === type.id ? 'fa-check' : 'fa-link'"></i>
              {{ linkGeneratingId === type.id ? 'Generating...' : linkCopiedId === type.id ? 'Copied!' : 'Grab a Link' }}
            </button>
            <button class="oa-btn oa-btn-delete" @click="deleteType(index, type.isTemplate)">
              <i class="fa fa-trash-o"></i> Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <AppointmentTypeModal
      ref="aptModal"
      :procedureCodes="procedureCodes"
      :providers="providers"
      :operatories="operatories"
      :blockoutTypes="blockoutTypes"
      :editType="editingType"
      :saving="saving"
      @saved="onTypeSaved"
    />

    <!-- Copy Settings Modal -->
    <CopySettingsModal
      ref="copyModal"
      :locations="paidLocations"
      :defaultToSiteId="selectedSiteId"
      @copied="onSettingsCopied"
    />

    <!-- UTM Link Builder Popover -->
    <UtmLinkBuilder
      :visible="showLinkBuilder"
      :shortUrl="linkBuilderShortUrl"
      :label="linkBuilderLabel"
      @close="showLinkBuilder = false"
      @copied="onLinkBuilderCopied"
    />
  </section>
</template>

<script>
/* eslint-disable */
import axios from 'axios';
import AppointmentTypeModal from './AppointmentTypeModal.vue';
import LocationSelector from './LocationSelector.vue';
import CopySettingsModal from './CopySettingsModal.vue';
import UtmLinkBuilder from './UtmLinkBuilder.vue';

export default {
  name: 'AppointmentTypes',
  components: { AppointmentTypeModal, LocationSelector, CopySettingsModal, UtmLinkBuilder },
  props: {
    appointmentTypes: { type: Array, default: () => [] },
    procedureCodes: { type: Array, default: () => [] },
    providers: { type: Array, default: () => [] },
    operatories: { type: Array, default: () => [] },
    blockoutTypes: { type: Array, default: () => [] },
    paidLocations: { type: Array, default: () => [] },
    locationStatus: { type: Object, default: () => ({}) },
    currentSiteId: { type: String, default: '' },
    saving: { type: Boolean, default: false }
  },

  data() {
    return {
      selectedSiteId: '',
      localTypes: [],
      editingType: null,
      editingIndex: -1,
      // Default templates for new offices
      defaultTemplates: [
        {
          id: 'template-1',
          name: 'New Patient - Adult 14+',
          description: 'Comprehensive exam and cleaning for new patients',
          duration: 90,
          procedureCodes: ['D0150', 'D1110'],
          operatoryProviders: [],
          isTemplate: true
        },
        {
          id: 'template-2',
          name: 'New Patient: 8-13 Y.O',
          description: 'Comprehensive exam and cleaning for new patients',
          duration: 60,
          procedureCodes: ['D0150', 'D1120'],
          operatoryProviders: [],
          isTemplate: true
        },
        {
          id: 'template-3',
          name: 'Emergency Exam',
          description: 'Limited examination',
          duration: 45,
          procedureCodes: ['D9110'],
          operatoryProviders: [],
          isTemplate: true
        }
      ],
      // Track which templates have been dismissed/deleted
      dismissedTemplates: [],
      // Track which type just had its link copied (for "Copied!" feedback)
      linkCopiedId: null,
      linkGeneratingId: null,
      // UTM Link Builder
      showLinkBuilder: false,
      linkBuilderShortUrl: '',
      linkBuilderLabel: ''
    };
  },

  computed: {
    hasMultipleLocations() {
      return this.paidLocations && this.paidLocations.length > 1;
    },

    // Show templates only when there are no saved appointment types
    showTemplates() {
      return this.localTypes.length === 0 && this.visibleTemplates.length > 0;
    },

    // Templates that haven't been dismissed
    visibleTemplates() {
      return this.defaultTemplates.filter(t => !this.dismissedTemplates.includes(t.id));
    },

    // Combined display list: either templates or real types
    displayTypes() {
      if (this.showTemplates) {
        return this.visibleTemplates;
      }
      return this.localTypes;
    }
  },

  watch: {
    currentSiteId: {
      handler(newVal) {
        if (newVal && newVal !== this.selectedSiteId) {
          this.selectedSiteId = newVal;
        }
      },
      immediate: true
    },

    selectedSiteId(newVal, oldVal) {
      if (newVal && newVal !== oldVal && oldVal !== '') {
        this.$emit('location-changed', newVal);
      }
    },

    appointmentTypes: {
      handler(newVal) {
        this.localTypes = newVal ? JSON.parse(JSON.stringify(newVal)) : [];
      },
      immediate: true,
      deep: true
    }
  },

  methods: {
    getIconClass(name) {
      const n = (name || '').toLowerCase();
      if (n.includes('new patient') || n.includes('exam')) return 'oa-icon-blue';
      if (n.includes('cleaning') || n.includes('routine')) return 'oa-icon-green';
      if (n.includes('emergency')) return 'oa-icon-red';
      if (n.includes('consultation')) return 'oa-icon-purple';
      return 'oa-icon-blue';
    },

    getIconFA(name) {
      const n = (name || '').toLowerCase();
      if (n.includes('new patient') || n.includes('exam')) return 'fa fa-file-text';
      if (n.includes('cleaning') || n.includes('routine')) return 'fa fa-check-circle';
      if (n.includes('emergency')) return 'fa fa-star';
      if (n.includes('consultation')) return 'fa fa-comments';
      return 'fa fa-calendar';
    },

    openModal(index, isTemplate = false) {
      if (typeof index === 'number' && index >= 0) {
        this.editingIndex = index;
        if (isTemplate || this.showTemplates) {
          // Editing a template - pass template data but mark it for conversion
          const template = this.visibleTemplates[index];
          this.editingType = JSON.parse(JSON.stringify(template));
          this.editingType.isTemplate = true; // Mark so modal knows it's a template
        } else {
          this.editingType = JSON.parse(JSON.stringify(this.localTypes[index]));
        }
      } else {
        this.editingIndex = -1;
        this.editingType = null;
      }
      this.$refs.aptModal.show();
    },

    onTypeSaved(appointmentType) {
      // Check if this was a template being saved (converted to real type)
      if (this.editingType && this.editingType.isTemplate) {
        // Dismiss the template and add as a real type
        this.dismissedTemplates.push(this.editingType.id);
        // Remove the isTemplate flag and give it a new ID
        delete appointmentType.isTemplate;
        appointmentType.id = `apt-${Date.now()}`;
        this.localTypes.push(appointmentType);
      } else if (this.editingIndex >= 0 && !this.showTemplates) {
        // Editing existing real type
        this.$set(this.localTypes, this.editingIndex, appointmentType);
      } else {
        // Creating new type
        this.localTypes.push(appointmentType);
      }
      this.editingIndex = -1;
      this.editingType = null;
      this.$emit('save', [...this.localTypes]);
    },

    deleteType(index, isTemplate = false) {
      if (isTemplate || this.showTemplates) {
        // Deleting a template - just dismiss it
        const template = this.visibleTemplates[index];
        if (!confirm(`Remove "${template.name}" template? You can recreate it later by adding a new appointment type.`)) return;
        this.dismissedTemplates.push(template.id);
      } else {
        // Deleting a real type
        if (!confirm(`Delete "${this.localTypes[index].name}"? This cannot be undone.`)) return;
        this.localTypes.splice(index, 1);
        this.$emit('save', [...this.localTypes]);
      }
    },

    showCopyModal() {
      this.$refs.copyModal.show();
    },

    onSettingsCopied({ toSiteId }) {
      if (toSiteId === this.selectedSiteId) {
        this.$emit('location-changed', this.selectedSiteId);
      }
    },

    async grabLink(type) {
      const siteId = this.hasMultipleLocations ? this.selectedSiteId : this.currentSiteId;
      const bookingUrl = `${window.location.origin}/online-scheduling/book-appointment.html?siteID=${siteId}&apt=${type.id}`;

      this.linkGeneratingId = type.id;
      let shortUrl = bookingUrl; // fallback to full URL

      try {
        const response = await axios.post('/get-booking-short-url', {
          siteID: siteId,
          bookingUrl: bookingUrl
        }, { crossdomain: true });
        const result = typeof response.data === 'string' ? response.data : (response.data && response.data.data);
        if (result) {
          shortUrl = result;
        }
      } catch (err) {
        console.error('Error generating short URL for appointment type:', err);
      }

      this.linkGeneratingId = null;

      // Open UTM Link Builder popover
      this.linkBuilderShortUrl = shortUrl;
      this.linkBuilderLabel = type.name || 'Booking Link';
      this.showLinkBuilder = true;
    },

    onLinkBuilderCopied() {
      this.$emit('link-copied');
    }
  }
};
</script>
